import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  Plus,
  Package,
  Layers,
  BarChart3,
  Calendar,
  DollarSign,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  MapPin,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Filter,
  ArrowUpDown,
  X,
  Eye,
  GripVertical,
  Users,
  UserPlus,
  User,
  FileText,
  Download,
  Pencil,
  Paperclip,
  Image,
  Upload,
  CheckSquare,
  Square,
  Copy,
  LayoutList,
  BarChart2
} from 'lucide-react';
import PhaseGanttChart from '@/components/PhaseGanttChart';
import PhasesTab from '@/components/project/PhasesTab';
import DocumentsTab from '@/components/project/DocumentsTab';
import OrdersTab from '@/components/project/OrdersTab';
import AnalyticsTab from '@/components/project/AnalyticsTab';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import OrderDialogs from '@/components/project/OrderDialogs';
import { EditProjectDialog } from '@/components/project/EditProjectDialog';
import { NewPhaseDialog } from '@/components/project/NewPhaseDialog';
import { TeamAssignDialog } from '@/components/project/TeamAssignDialog';
import { DeleteConfirmDialog } from '@/components/project/DeleteConfirmDialog';
import ProductDetailModal from '@/components/products/ProductDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('phases');
  const [expandedPhase, setExpandedPhase] = useState(null);

  // Phases tab view mode ('chart' or 'list')
  const [phasesView, setPhasesView] = useState(() => {
    if (typeof window === 'undefined') return 'chart';
    return localStorage.getItem('phasesView') || 'chart';
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isTinyScreen, setIsTinyScreen] = useState(false);

  useEffect(() => {
    const update = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTinyScreen(window.innerWidth < 480);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handlePhasesViewChange = (v) => {
    setPhasesView(v);
    try { localStorage.setItem('phasesView', v); } catch (err) { console.warn('localStorage set phasesView failed:', err); }
  };

  // Team assignment dialog
  const [showTeamAssign, setShowTeamAssign] = useState(false);
  const [assignedSelection, setAssignedSelection] = useState([]);

  // Project documents
  const [projectDocuments, setProjectDocuments] = useState([]);

  // Project analytics
  const [projectAnalytics, setProjectAnalytics] = useState(null);

  // Order filters and sorting
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderVendorFilter, setOrderVendorFilter] = useState('all');
  const [orderSort, setOrderSort] = useState('date-desc');

  // Dialog states
  const [showEditProject, setShowEditProject] = useState(false);
  const [showNewPhase, setShowNewPhase] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(null);
  const [showEditOrder, setShowEditOrder] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [orderTemplates, setOrderTemplates] = useState([]);
  const [showTemplatesPicker, setShowTemplatesPicker] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(null); // { order, name, category, description }
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null); // { order, target_project_id, target_phase_id }
  const [duplicating, setDuplicating] = useState(false);
  const [companyProjects, setCompanyProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'project'|'phase'|'order', id, name }
  const [newPhase, setNewPhase] = useState({ 
    name: '', 
    budget: '', 
    status: 'Not Started',
    start_date: '',
    end_date: '',
    description: ''
  });
  
  // New order form state
  const [newOrder, setNewOrder] = useState({
    vendor_name: '',
    description: '',
    po_number: '',
    phase_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    status: 'Draft',
    tax: 0,
    freight: 0,
    line_items: [{ _key: `li-init-${Math.random().toString(36).slice(2, 10)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 }]
  });

  // Vendor autocomplete
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);

  // Price benchmarks for line items
  const [priceBenchmarks, setPriceBenchmarks] = useState({});
  const benchmarkTimers = useRef({});

  // Savings analysis & waitlist
  const [savingsData, setSavingsData] = useState(null);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  // BOM fulfillment map for Gantt indicator
  const [bomFulfillmentMap, setBomFulfillmentMap] = useState({});

  // Product Detail modal state
  const [activeProductId, setActiveProductId] = useState(null);

  const refreshBomMap = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/phases/project/${projectId}/bom/fulfillment-map`,
        { withCredentials: true },
      );
      setBomFulfillmentMap(res.data || {});
    } catch (e) {
      // Non-fatal — just leave map empty
      console.warn('Failed to load BOM fulfillment map', e);
    }
  }, [projectId]);

  const fetchProjectData = useCallback(async () => {
    try {
      const [projectRes, phasesRes, ordersRes, vendorsRes, teamRes, docsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/phases/project/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/orders/project/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/orders/vendors/search`, { withCredentials: true }),
        axios.get(`${API}/settings/team`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/documents/project/${projectId}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/project/${projectId}`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setProject(projectRes.data);
      setPhases(phasesRes.data);
      setOrders(ordersRes.data);
      setVendors(vendorsRes.data);
      setTeamMembers(teamRes.data.filter(m => m.status === 'Active'));
      setProjectDocuments(docsRes.data);
      if (analyticsRes.data) setProjectAnalytics(analyticsRes.data);
      // Fetch order templates
      axios.get(`${API}/orders/templates/list`, { withCredentials: true })
        .then(res => setOrderTemplates(res.data || []))
        .catch(() => {});
      // Fetch BOM fulfillment map (non-blocking)
      refreshBomMap();
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate, refreshBomMap]);

  // Fetch savings data and waitlist status when analytics tab is active
  useEffect(() => {
    if (activeTab === 'analytics' && projectId) {
      axios.get(`${API}/pricing/project/${projectId}/savings`, { withCredentials: true })
        .then(res => setSavingsData(res.data))
        .catch(() => {});
      axios.get(`${API}/waitlist/status`, { withCredentials: true })
        .then(res => setWaitlistStatus(res.data))
        .catch(() => {});
    }
  }, [activeTab, projectId]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Filter by status
    if (orderStatusFilter !== 'all') {
      result = result.filter(o => o.status === orderStatusFilter);
    }
    
    // Filter by vendor
    if (orderVendorFilter !== 'all') {
      result = result.filter(o => o.vendor_name === orderVendorFilter);
    }
    
    // Sort
    switch (orderSort) {
      case 'date-desc':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'date-asc':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'amount-desc':
        result.sort((a, b) => b.total - a.total);
        break;
      case 'amount-asc':
        result.sort((a, b) => a.total - b.total);
        break;
      default:
        break;
    }
    
    return result;
  }, [orders, orderStatusFilter, orderVendorFilter, orderSort]);

  // Get unique vendors from orders
  const orderVendors = useMemo(() => {
    return [...new Set(orders.map(o => o.vendor_name))].sort();
  }, [orders]);

  // Fetch attachments when order detail opens
  useEffect(() => {
    if (showOrderDetail?.order_id) {
      fetchAttachments(showOrderDetail.order_id);
    } else {
      setAttachments([]);
    }
  }, [showOrderDetail?.order_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, {
        ...editingProject,
        total_budget: parseFloat(editingProject.total_budget) || 0
      }, { withCredentials: true });
      setProject(response.data);
      setShowEditProject(false);
      toast.success('Project updated!');
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/phases/`, {
        project_id: projectId,
        name: newPhase.name,
        budget: parseFloat(newPhase.budget) || 0,
        status: newPhase.status,
        start_date: newPhase.start_date || null,
        end_date: newPhase.end_date || null,
        description: newPhase.description || null,
        sort_order: phases.length
      }, { withCredentials: true });
      setPhases([...phases, response.data]);
      setShowNewPhase(false);
      setNewPhase({ name: '', budget: '', status: 'Not Started', start_date: '', end_date: '', description: '' });
      toast.success('Phase created!');
    } catch (error) {
      toast.error('Failed to create phase');
    }
  };

  const handleUpdatePhaseStatus = async (phaseId, newStatus) => {
    try {
      const response = await axios.put(`${API}/phases/${phaseId}`, { status: newStatus }, { withCredentials: true });
      setPhases(phases.map(p => p.phase_id === phaseId ? response.data : p));
      toast.success('Phase updated!');
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  const handleUpdatePhaseDates = async (phaseId, startDate, endDate) => {
    // Optimistic update
    const prev = phases;
    setPhases(phases.map(p => p.phase_id === phaseId ? { ...p, start_date: startDate, end_date: endDate } : p));
    try {
      const response = await axios.put(
        `${API}/phases/${phaseId}`,
        { start_date: startDate, end_date: endDate },
        { withCredentials: true }
      );
      setPhases(curr => curr.map(p => p.phase_id === phaseId ? response.data : p));
      toast.success('Phase dates updated');
    } catch (error) {
      setPhases(prev);
      toast.error('Failed to update phase dates');
    }
  };

  const handleDeletePhase = async (phaseId) => {
    try {
      await axios.delete(`${API}/phases/${phaseId}`, { withCredentials: true });
      setPhases(phases.filter(p => p.phase_id !== phaseId));
      toast.success('Phase deleted');
    } catch (error) {
      toast.error('Failed to delete phase');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Calculate running total for new order
  const newOrderSubtotal = useMemo(() => {
    return newOrder.line_items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
  }, [newOrder.line_items]);

  const newOrderTotal = useMemo(() => {
    return newOrderSubtotal + (parseFloat(newOrder.tax) || 0) + (parseFloat(newOrder.freight) || 0);
  }, [newOrderSubtotal, newOrder.tax, newOrder.freight]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    // Inline validation
    if (!newOrder.vendor_name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    const validItems = newOrder.line_items.filter(li => li.product_description);
    if (validItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    try {
      const response = await axios.post(`${API}/orders/`, {
        project_id: projectId,
        ...newOrder,
        phase_id: newOrder.phase_id || null,
        tax: parseFloat(newOrder.tax) || 0,
        freight: parseFloat(newOrder.freight) || 0,
        line_items: newOrder.line_items.filter(li => li.product_description).map(li => ({
          ...li,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0
        }))
      }, { withCredentials: true });
      setOrders([...orders, response.data]);
      
      // If order is Delivered, refresh project to update spent
      if (response.data.status === 'Delivered') {
        fetchProjectData();
      }
      
      // Add vendor to list if new
      if (!vendors.includes(newOrder.vendor_name)) {
        setVendors([...vendors, newOrder.vendor_name].sort());
      }
      
      setShowNewOrder(false);
      setNewOrder({
        vendor_name: '',
        description: '',
        po_number: '',
        phase_id: '',
        order_date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        status: 'Draft',
        tax: 0,
        freight: 0,
        line_items: [{ _key: `li-reset-${Math.random().toString(36).slice(2, 10)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 }]
      });
      toast.success('Order created!');
    } catch (error) {
      console.error('create order failed:', error);
      toast.error('Failed to create order');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus }, { withCredentials: true });
      setOrders(orders.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
      
      // If status changed to/from Delivered, refresh to update spent amounts
      const oldOrder = orders.find(o => o.order_id === orderId);
      if (newStatus === 'Delivered' || oldOrder?.status === 'Delivered') {
        fetchProjectData();
      }
      
      toast.success('Order updated!');
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const deletedOrder = orders.find(o => o.order_id === orderId);
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrders(orders.filter(o => o.order_id !== orderId));
      
      // If deleted order was Delivered, refresh to update spent
      if (deletedOrder?.status === 'Delivered') {
        fetchProjectData();
      }
      
      setShowOrderDetail(null);
      toast.success('Order deleted');
    } catch (error) {
      toast.error('Failed to delete order');
    } finally {
      setConfirmDelete(null);
    }
  };

  const openEditOrder = (order) => {
    setShowEditOrder({
      vendor_name: order.vendor_name || '',
      description: order.description || '',
      po_number: order.po_number || '',
      phase_id: order.phase_id || '',
      order_date: order.order_date ? order.order_date.split('T')[0] : '',
      expected_delivery_date: order.expected_delivery_date ? order.expected_delivery_date.split('T')[0] : '',
      status: order.status || 'Draft',
      tax: order.tax || 0,
      freight: order.freight || 0,
      notes: order.notes || '',
      line_items: order.line_items && order.line_items.length > 0
        ? order.line_items.map(li => ({
            line_item_id: li.line_item_id,
            product_description: li.product_description,
            quantity: li.quantity,
            unit: li.unit,
            unit_price: li.unit_price,
          }))
        : [{ product_description: '', quantity: 1, unit: 'each', unit_price: 0 }],
      order_id: order.order_id,
    });
    setShowOrderDetail(null);
  };

  const handleSaveEditOrder = async (e) => {
    e.preventDefault();
    if (!showEditOrder) return;
    if (!showEditOrder.vendor_name.trim()) { toast.error('Vendor name is required'); return; }
    const validItems = showEditOrder.line_items.filter(li => li.product_description.trim());
    if (validItems.length === 0) { toast.error('Add at least one line item'); return; }

    try {
      const response = await axios.put(`${API}/orders/${showEditOrder.order_id}/full`, {
        vendor_name: showEditOrder.vendor_name,
        description: showEditOrder.description || null,
        po_number: showEditOrder.po_number || null,
        phase_id: showEditOrder.phase_id || null,
        order_date: showEditOrder.order_date || null,
        expected_delivery_date: showEditOrder.expected_delivery_date || null,
        status: showEditOrder.status,
        tax: parseFloat(showEditOrder.tax) || 0,
        freight: parseFloat(showEditOrder.freight) || 0,
        notes: showEditOrder.notes || null,
        line_items: validItems.map(li => ({
          line_item_id: li.line_item_id || undefined,
          product_description: li.product_description,
          quantity: parseFloat(li.quantity) || 0,
          unit: li.unit || 'each',
          unit_price: parseFloat(li.unit_price) || 0,
        })),
      }, { withCredentials: true });

      setOrders(orders.map(o => o.order_id === response.data.order_id ? response.data : o));
      if (response.data.status === 'Delivered' || orders.find(o => o.order_id === response.data.order_id)?.status === 'Delivered') {
        fetchProjectData();
      }
      if (!vendors.includes(showEditOrder.vendor_name)) {
        setVendors([...vendors, showEditOrder.vendor_name].sort());
      }
      setShowEditOrder(null);
      toast.success('Order updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const editOrderSubtotal = showEditOrder
    ? showEditOrder.line_items.reduce((sum, li) => sum + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0)
    : 0;
  const editOrderTotal = editOrderSubtotal + (parseFloat(showEditOrder?.tax) || 0) + (parseFloat(showEditOrder?.freight) || 0);

  // ---------- Save existing order as template ----------
  const openSaveAsTemplate = (order) => {
    setSaveAsTemplate({
      order,
      name: order.po_number ? `${order.vendor_name} - ${order.po_number}` : `${order.vendor_name} template`,
      category: order.category || 'general',
      description: order.description || '',
    });
  };

  const handleSaveAsTemplate = async () => {
    if (!saveAsTemplate) return;
    const { order, name, category, description } = saveAsTemplate;
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    setSavingTemplate(true);
    try {
      const response = await axios.post(`${API}/orders/templates`, {
        name: name.trim(),
        description: description.trim() || null,
        category: category || 'general',
        vendor_name: order.vendor_name || null,
        line_items: (order.line_items || []).map(li => ({
          product_description: li.product_description,
          quantity: parseFloat(li.quantity) || 0,
          unit: li.unit || 'each',
          unit_price: parseFloat(li.unit_price) || 0,
        })),
        tax: parseFloat(order.tax) || 0,
        freight: parseFloat(order.freight) || 0,
      }, { withCredentials: true });
      setOrderTemplates(prev => [...prev, response.data]);
      toast.success(`Template "${name.trim()}" saved`);
      setSaveAsTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDownloadBudgetPDF = async () => {
    try {
      const res = await axios.get(`${API}/analytics/project/${projectId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : `Budget_Summary.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Budget PDF downloaded!');
    } catch {
      toast.error('Failed to generate Budget PDF');
    }
  };

  const handleDownloadPDF = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : `PO_${orderId}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const fetchAttachments = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/attachments`, { withCredentials: true });
      setAttachments(res.data || []);
    } catch {
      setAttachments([]);
    }
  };

  const handleUploadAttachment = async (orderId, files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 20MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/orders/${orderId}/attachments`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} file${uploaded > 1 ? 's' : ''} uploaded`);
      fetchAttachments(orderId);
    }
    setUploading(false);
  };

  const handleDeleteAttachment = async (attachmentId, orderId) => {
    try {
      await axios.delete(`${API}/orders/attachments/${attachmentId}`, { withCredentials: true });
      toast.success('Attachment deleted');
      fetchAttachments(orderId);
    } catch {
      toast.error('Failed to delete attachment');
    }
  };

  // Lazy-fetch company projects with phases when the Duplicate dialog opens
  useEffect(() => {
    if (!duplicateTarget || companyProjects.length > 0) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/timeline/`, { withCredentials: true });
        setCompanyProjects(res.data || []);
      } catch {
        toast.error('Failed to load projects list');
      }
    })();
  }, [duplicateTarget, companyProjects.length]);

  const handleDuplicateOrder = async () => {
    if (!duplicateTarget?.target_project_id) return;
    setDuplicating(true);
    try {
      const res = await axios.post(
        `${API}/orders/${duplicateTarget.order.order_id}/duplicate`,
        {
          target_project_id: duplicateTarget.target_project_id,
          target_phase_id: duplicateTarget.target_phase_id || null,
        },
        { withCredentials: true }
      );
      const targetName = companyProjects.find(p => p.project_id === duplicateTarget.target_project_id)?.name || 'project';
      toast.success(`Duplicated to ${targetName}`);
      setDuplicateTarget(null);
      // If user duplicated INTO current project, refresh orders
      if (res.data?.project_id === projectId) {
        fetchProjectData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate order');
    } finally {
      setDuplicating(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.order_id));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedOrders.length === 0) return;
    try {
      const res = await axios.put(`${API}/orders/bulk-status`, {
        order_ids: selectedOrders,
        status: bulkStatus,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setSelectedOrders([]);
      setBulkStatus('');
      fetchProjectData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update orders');
    }
  };

  const applyOrderTemplate = (template) => {
    setNewOrder({
      ...newOrder,
      vendor_name: template.vendor_name || newOrder.vendor_name,
      description: template.description || '',
      tax: template.tax || 0,
      freight: template.freight || 0,
      line_items: template.line_items.length > 0
        ? template.line_items.map((li, idx) => ({
            _key: `li-tmpl-${Date.now()}-${idx}`,
            product_description: li.product_description,
            quantity: li.quantity,
            unit: li.unit || 'each',
            unit_price: li.unit_price,
          }))
        : [{ _key: `li-tmpl-empty-${Math.random().toString(36).slice(2, 10)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 }],
    });
    setShowTemplatesPicker(false);
    setShowNewOrder(true);
    toast.success(`Template "${template.name}" applied`);
    // Track usage (fire-and-forget)
    axios.post(`${API}/orders/templates/${template.template_id}/use`, {}, { withCredentials: true }).catch(() => {});
  };

  const addLineItem = () => {
    setNewOrder({
      ...newOrder,
      line_items: [...newOrder.line_items, { _key: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    setNewOrder(prev => {
      const updated = [...prev.line_items];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, line_items: updated };
    });
  };

  const updateLineItemFields = (index, patch) => {
    setNewOrder(prev => {
      const updated = [...prev.line_items];
      updated[index] = { ...updated[index], ...patch };
      return { ...prev, line_items: updated };
    });
  };

  const removeLineItem = (index) => {
    if (newOrder.line_items.length === 1) return;
    setNewOrder({
      ...newOrder,
      line_items: newOrder.line_items.filter((_, i) => i !== index)
    });
  };

  const moveLineItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newOrder.line_items.length) return;
    
    const updated = [...newOrder.line_items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setNewOrder({ ...newOrder, line_items: updated });
  };

  // Team assignment
  const handleOpenTeamAssign = () => {
    setAssignedSelection(project?.assigned_users || []);
    setShowTeamAssign(true);
  };

  const toggleUserAssignment = (userId) => {
    setAssignedSelection(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSaveAssignments = async () => {
    try {
      const response = await axios.put(`${API}/projects/${projectId}/assign`, {
        user_ids: assignedSelection
      }, { withCredentials: true });
      setProject(response.data);
      setShowTeamAssign(false);
      toast.success('Team assignments updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignments');
    }
  };

  const getTeamMember = (userId) => teamMembers.find(m => m.user_id === userId);

  const isForeman = user?.role === 'Foreman';
  const canManageTeam = user?.role === 'Owner' || user?.role === 'Project Manager';

  // Price benchmark lookup (debounced) — uses product_id when available for exact match
  const fetchBenchmark = useCallback((productDesc, index, productId = null) => {
    if (benchmarkTimers.current[index]) {
      clearTimeout(benchmarkTimers.current[index]);
    }
    if (!productDesc || productDesc.trim().length < 3) {
      setPriceBenchmarks(prev => { const n = {...prev}; delete n[index]; return n; });
      return;
    }
    benchmarkTimers.current[index] = setTimeout(async () => {
      try {
        const params = { product_description: productDesc.trim() };
        if (productId) params.product_id = productId;
        const res = await axios.get(`${API}/pricing/benchmark`, {
          params,
          withCredentials: true
        });
        if (res.data) {
          setPriceBenchmarks(prev => ({ ...prev, [index]: res.data }));
        } else {
          setPriceBenchmarks(prev => { const n = {...prev}; delete n[index]; return n; });
        }
      } catch {
        setPriceBenchmarks(prev => { const n = {...prev}; delete n[index]; return n; });
      }
    }, 500);
  }, []);

  const getPriceIndicator = (unitPrice, benchmark) => {
    if (!benchmark || !unitPrice || unitPrice <= 0) return null;
    const price = parseFloat(unitPrice);
    const avg = benchmark.avg_price;
    const pctAbove = ((price - avg) / avg) * 100;
    
    let color, label;
    if (price <= avg) {
      color = 'text-success';
      label = 'Good price';
    } else if (pctAbove <= 15) {
      color = 'text-orange-500';
      label = `${pctAbove.toFixed(0)}% above avg`;
    } else {
      color = 'text-destructive';
      label = `${pctAbove.toFixed(0)}% above avg`;
    }
    return { color, label, avg, low: benchmark.low_price, high: benchmark.high_price, count: benchmark.count };
  };

  const handleJoinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      const res = await axios.post(`${API}/waitlist/`, {}, { withCredentials: true });
      setWaitlistStatus({ on_waitlist: true });
      toast.success(res.data.message);
    } catch {
      toast.error('Failed to join waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPhaseStatusClass = (status) => {
    switch (status) {
      case 'Not Started': return 'bg-zinc-100 text-zinc-700';
      case 'In Progress': return 'bg-blue-500 text-white';
      case 'Complete': return 'bg-green-500 text-white';
      case 'Delayed': return 'bg-red-500 text-white';
      default: return 'bg-zinc-100';
    }
  };

  const getPhaseTimelineColor = (status) => {
    switch (status) {
      case 'Not Started': return 'bg-gray-300';
      case 'In Progress': return 'bg-blue-500';
      case 'Complete': return 'bg-green-500';
      case 'Delayed': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getOrderStatusClass = (status) => {
    switch (status) {
      case 'Draft': return 'bg-zinc-100 text-zinc-700';
      case 'Ordered': return 'bg-blue-500 text-white';
      case 'Confirmed': return 'bg-purple-500 text-white';
      case 'Shipped': return 'bg-orange-500 text-zinc-900';
      case 'Delivered': return 'bg-green-500 text-white';
      case 'Issue': return 'bg-red-500 text-white';
      default: return 'bg-zinc-100';
    }
  };

  // Filter vendor suggestions
  const vendorSuggestions = useMemo(() => {
    if (!vendorSearch) return vendors;
    return vendors.filter(v => v.toLowerCase().includes(vendorSearch.toLowerCase()));
  }, [vendors, vendorSearch]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 animate-pulse" data-testid="project-skeleton">
        <div className="h-6 w-32 bg-zinc-100 mb-6" />
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="h-8 w-64 bg-zinc-100 mb-2" />
            <div className="h-4 w-48 bg-zinc-50 mb-1" />
            <div className="h-4 w-36 bg-zinc-50" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white border border-zinc-200 p-4">
              <div className="h-3 w-20 bg-zinc-50 mb-2" />
              <div className="h-6 w-24 bg-zinc-100" />
            </div>
          ))}
        </div>
        <div className="h-12 bg-zinc-100 border border-zinc-200 mb-6" />
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-white border border-zinc-200" />)}
        </div>
      </div>
    );
  }

  if (!project) return null;

  const budgetPercent = project.total_budget > 0 ? (project.spent_amount / project.total_budget) * 100 : 0;
  const budgetStatus = budgetPercent >= 90 ? 'critical' : budgetPercent >= 70 ? 'warning' : 'good';
  const remaining = project.total_budget - project.spent_amount;

  // Check if phase budgets exceed project budget
  const totalPhaseBudget = phases.reduce((sum, p) => sum + p.budget, 0);
  const phaseBudgetExceeds = totalPhaseBudget > project.total_budget;

  // Analytics data
  const deliveredOrders = orders.filter(o => o.status === 'Delivered').length;
  const pendingOrders = orders.filter(o => !['Delivered', 'Issue'].includes(o.status)).length;
  const completedPhases = phases.filter(p => p.status === 'Complete').length;

  // Sort phases by start_date for timeline
  const sortedPhases = [...phases].sort((a, b) => {
    if (!a.start_date && !b.start_date) return a.sort_order - b.sort_order;
    if (!a.start_date) return 1;
    if (!b.start_date) return -1;
    return new Date(a.start_date) - new Date(b.start_date);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="project-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm font-bold mb-4 hover:text-primary"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide" data-testid="project-name">{project.name}</h1>
              <span className={`px-3 py-1 text-xs font-bold uppercase border border-zinc-200 ${
                project.status === 'Active' ? 'bg-green-500 text-white' :
                project.status === 'On Hold' ? 'bg-yellow-500 text-zinc-900' : 'bg-zinc-500 text-white'
              }`}>
                {project.status}
              </span>
            </div>
            <p className="text-lg font-medium">{project.client_name}</p>
            {project.address && (
              <p className="text-zinc-500 flex items-center gap-2 mt-1">
                <MapPin className="w-4 h-4" />
                {project.address}
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
              <span className="uppercase font-bold">{project.project_type}</span>
              {(project.start_date || project.end_date) && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(project.start_date)} - {formatDate(project.end_date) || 'TBD'}
                </span>
              )}
            </div>

            {/* Assigned Team Members */}
            <div className="flex items-center gap-3 mt-4" data-testid="project-team-section">
              <div className="flex -space-x-2">
                {(project.assigned_users || []).slice(0, 6).map((userId) => {
                  const member = getTeamMember(userId);
                  if (!member) return null;
                  return (
                    <div key={userId} className="relative" title={`${member.name} (${member.role})`}>
                      {member.picture ? (
                        <img 
                          src={member.picture} 
                          alt={member.name}
                          className="w-9 h-9 rounded-full border-2 border-white object-cover"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full border-2 border-white bg-zinc-700 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">
                            {member.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {(project.assigned_users || []).length > 6 && (
                  <div className="w-9 h-9 rounded-full border-2 border-white bg-gray-400 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      +{project.assigned_users.length - 6}
                    </span>
                  </div>
                )}
              </div>
              {canManageTeam && (
                <button
                  onClick={handleOpenTeamAssign}
                  data-testid="manage-team-btn"
                  className="h-9 px-3 bg-white text-zinc-900 text-xs font-bold uppercase border border-zinc-200 hover:bg-zinc-50 flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Team
                </button>
              )}
              <span className="text-xs text-zinc-500">
                {(project.assigned_users || []).length} member{(project.assigned_users || []).length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {!isForeman && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditingProject({ ...project });
                  setShowEditProject(true);
                }}
                data-testid="edit-project-btn"
                className="h-12 px-4 bg-white text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200 hover:bg-zinc-50 transition-colors flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => setConfirmDelete({ type: 'project', id: projectId, name: project.name })}
                data-testid="delete-project-btn"
                className="h-12 px-4 bg-white text-destructive font-bold uppercase tracking-wide border border-zinc-200 hover:bg-destructive hover:text-white transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Budget Summary Card */}
      <div className={`border border-zinc-200 p-4 sm:p-6  mb-6 ${
        budgetStatus === 'critical' ? 'bg-red-50' :
        budgetStatus === 'warning' ? 'bg-yellow-50' : 'bg-white'
      }`} data-testid="budget-summary">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Budget Overview</p>
          <button
            onClick={handleDownloadBudgetPDF}
            data-testid="download-budget-pdf-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 bg-zinc-900 text-white text-xs font-bold uppercase border border-zinc-200 hover:bg-zinc-800 active:translate-y-0.5 transition-all"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <p className="text-xs font-bold uppercase text-zinc-500 mb-1">Total Budget</p>
            <p className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide">{formatCurrency(project.total_budget)}</p>
          </div>
          <div className="text-center border-l-2 border-r-2 border-black">
            <p className="text-xs font-bold uppercase text-zinc-500 mb-1">Spent</p>
            <p className={`text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide ${
              budgetStatus === 'critical' ? 'text-destructive' :
              budgetStatus === 'warning' ? 'text-yellow-600' : 'text-zinc-900'
            }`}>
              {formatCurrency(project.spent_amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs font-bold uppercase text-zinc-500 mb-1">Remaining</p>
            <p className={`text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide ${remaining < 0 ? 'text-destructive' : 'text-success'}`}>
              {formatCurrency(remaining)}
            </p>
          </div>
        </div>

        {/* Budget Bar */}
        <div className="h-6 bg-zinc-100 border border-zinc-200 overflow-hidden">
          <div 
            className={`h-full transition-all ${
              budgetStatus === 'critical' ? 'bg-red-500' :
              budgetStatus === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(budgetPercent, 100)}%` }}
          />
        </div>
        <p className={`text-sm font-bold mt-2 text-center ${
          budgetStatus === 'critical' ? 'text-destructive' :
          budgetStatus === 'warning' ? 'text-yellow-600' : 'text-success'
        }`}>
          {budgetPercent.toFixed(0)}% of budget used
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full h-10 border border-zinc-200 bg-white p-0 rounded-none mb-6">
          <TabsTrigger 
            value="phases" 
            data-testid="phases-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 border-r-2 border-black"
          >
            <Layers className="w-5 h-5 mr-2" />
            Phases
          </TabsTrigger>
          <TabsTrigger 
            value="orders" 
            data-testid="orders-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 border-r-2 border-black"
          >
            <Package className="w-5 h-5 mr-2" />
            Orders
          </TabsTrigger>
          <TabsTrigger 
            value="analytics" 
            data-testid="analytics-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 border-r-2 border-black"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger 
            value="documents" 
            data-testid="documents-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900"
          >
            <FileText className="w-5 h-5 mr-2" />
            Docs{projectDocuments.length > 0 ? ` (${projectDocuments.length})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* Phases Tab */}
        <TabsContent value="phases" className="mt-0">
          <PhasesTab
            project={project}
            phases={phases}
            sortedPhases={sortedPhases}
            orders={orders}
            phaseBudgetExceeds={phaseBudgetExceeds}
            totalPhaseBudget={totalPhaseBudget}
            expandedPhase={expandedPhase}
            setExpandedPhase={setExpandedPhase}
            phasesView={phasesView}
            handlePhasesViewChange={handlePhasesViewChange}
            isMobile={isMobile}
            isTinyScreen={isTinyScreen}
            setShowNewPhase={setShowNewPhase}
            handleUpdatePhaseStatus={handleUpdatePhaseStatus}
            handleUpdatePhaseDates={handleUpdatePhaseDates}
            setConfirmDelete={setConfirmDelete}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getPhaseStatusClass={getPhaseStatusClass}
            getPhaseTimelineColor={getPhaseTimelineColor}
            bomFulfillmentMap={bomFulfillmentMap}
            refreshBomMap={refreshBomMap}
          />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-0">
          <OrdersTab
            orders={orders}
            filteredOrders={filteredOrders}
            phases={phases}
            orderTemplates={orderTemplates}
            orderStatusFilter={orderStatusFilter}
            setOrderStatusFilter={setOrderStatusFilter}
            orderVendorFilter={orderVendorFilter}
            setOrderVendorFilter={setOrderVendorFilter}
            orderVendors={orderVendors}
            orderSort={orderSort}
            setOrderSort={setOrderSort}
            selectedOrders={selectedOrders}
            setSelectedOrders={setSelectedOrders}
            toggleOrderSelection={toggleOrderSelection}
            toggleSelectAll={toggleSelectAll}
            bulkStatus={bulkStatus}
            setBulkStatus={setBulkStatus}
            handleBulkStatusUpdate={handleBulkStatusUpdate}
            setShowNewOrder={setShowNewOrder}
            setShowTemplatesPicker={setShowTemplatesPicker}
            setShowOrderDetail={setShowOrderDetail}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getOrderStatusClass={getOrderStatusClass}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-0">
          <AnalyticsTab
            project={project}
            phases={phases}
            orders={orders}
            projectAnalytics={projectAnalytics}
            savingsData={savingsData}
            waitlistStatus={waitlistStatus}
            joiningWaitlist={joiningWaitlist}
            handleJoinWaitlist={handleJoinWaitlist}
            completedPhases={completedPhases}
            pendingOrders={pendingOrders}
            deliveredOrders={deliveredOrders}
            getTeamMember={getTeamMember}
            getPhaseTimelineColor={getPhaseTimelineColor}
            formatCurrency={formatCurrency}
            onOpenProductDetail={(pid) => setActiveProductId(pid)}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-0">
          <DocumentsTab projectDocuments={projectDocuments} />
        </TabsContent>
      </Tabs>

      <EditProjectDialog
        open={showEditProject}
        onOpenChange={setShowEditProject}
        editingProject={editingProject}
        setEditingProject={setEditingProject}
        onSubmit={handleUpdateProject}
      />

      <NewPhaseDialog
        open={showNewPhase}
        onOpenChange={setShowNewPhase}
        newPhase={newPhase}
        setNewPhase={setNewPhase}
        onSubmit={handleCreatePhase}
      />

      <OrderDialogs
        showNewOrder={showNewOrder}
        setShowNewOrder={setShowNewOrder}
        newOrder={newOrder}
        setNewOrder={setNewOrder}
        vendorSearch={vendorSearch}
        setVendorSearch={setVendorSearch}
        showVendorSuggestions={showVendorSuggestions}
        setShowVendorSuggestions={setShowVendorSuggestions}
        vendorSuggestions={vendorSuggestions}
        newOrderSubtotal={newOrderSubtotal}
        newOrderTotal={newOrderTotal}
        handleCreateOrder={handleCreateOrder}
        addLineItem={addLineItem}
        removeLineItem={removeLineItem}
        moveLineItem={moveLineItem}
        updateLineItem={updateLineItem}
        updateLineItemFields={updateLineItemFields}
        fetchBenchmark={fetchBenchmark}
        getPriceIndicator={getPriceIndicator}
        priceBenchmarks={priceBenchmarks}
        showOrderDetail={showOrderDetail}
        setShowOrderDetail={setShowOrderDetail}
        attachments={attachments}
        uploading={uploading}
        handleUpdateOrderStatus={handleUpdateOrderStatus}
        handleDownloadPDF={handleDownloadPDF}
        handleUploadAttachment={handleUploadAttachment}
        handleDeleteAttachment={handleDeleteAttachment}
        openEditOrder={openEditOrder}
        openSaveAsTemplate={openSaveAsTemplate}
        setConfirmDelete={setConfirmDelete}
        canManageTeam={canManageTeam}
        showEditOrder={showEditOrder}
        setShowEditOrder={setShowEditOrder}
        editOrderSubtotal={editOrderSubtotal}
        editOrderTotal={editOrderTotal}
        handleSaveEditOrder={handleSaveEditOrder}
        showTemplatesPicker={showTemplatesPicker}
        setShowTemplatesPicker={setShowTemplatesPicker}
        orderTemplates={orderTemplates}
        applyOrderTemplate={applyOrderTemplate}
        saveAsTemplate={saveAsTemplate}
        setSaveAsTemplate={setSaveAsTemplate}
        savingTemplate={savingTemplate}
        handleSaveAsTemplate={handleSaveAsTemplate}
        duplicateTarget={duplicateTarget}
        setDuplicateTarget={setDuplicateTarget}
        duplicating={duplicating}
        handleDuplicateOrder={handleDuplicateOrder}
        companyProjects={companyProjects}
        phases={phases}
        onOpenProductDetail={(pid) => setActiveProductId(pid)}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        formatFileSize={formatFileSize}
        getOrderStatusClass={getOrderStatusClass}
      />

      <ProductDetailModal
        productId={activeProductId}
        open={!!activeProductId}
        onClose={() => setActiveProductId(null)}
      />

      <TeamAssignDialog
        open={showTeamAssign}
        onOpenChange={setShowTeamAssign}
        teamMembers={teamMembers}
        assignedSelection={assignedSelection}
        toggleUserAssignment={toggleUserAssignment}
        onSave={handleSaveAssignments}
      />

      <DeleteConfirmDialog
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        onConfirm={() => {
          if (confirmDelete?.type === 'project') handleDeleteProject();
          else if (confirmDelete?.type === 'phase') handleDeletePhase(confirmDelete.id);
          else if (confirmDelete?.type === 'order') handleDeleteOrder(confirmDelete.id);
        }}
      />
    </div>
  );
};

export default ProjectDetailPage;
